import CancelBid from '@/components/buttons/CancelBid';
import { CollectionOfferTraits } from '@/components/shared/CollectionOfferTraits/CollectionOfferTraits';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { isAttributeOffer, isSingleOrder, useUserOffers } from '@/hooks/use-user-offers';
import { useAuthentication } from '@/hooks/useAuthentication';
import { TokenSymbol } from '@/types/tokens';
import { routes } from '@/utils/routes';
import { Button, Collapse, Text, useDisclosure } from '@chakra-ui/react';
import { compareAddresses, formatAddress } from '@onbeam/utils';
import { ChevronDownIcon, CollectionOfferIcon, OfferIcon } from '@sphere/icons';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { AccountTableItemMobile } from '../AccountTableItemMobile';

export const AccountOfferItemMobile = ({
  offer,
  refetch,
}: {
  offer: ReturnType<typeof useUserOffers>['data'][number];
  refetch: () => void;
}) => {
  const { t } = useTranslation('account');
  const { t: tActivity } = useTranslation('activity');

  const marketplaceChain = useMarketplaceChain();
  const { userAddress } = useAuthentication();
  const { isOpen, onToggle } = useDisclosure();

  const isOwner = compareAddresses(userAddress, offer.maker);
  const collectionId = offer.criteria?.data?.collection?.id || offer.contract || '';

  const CancelButton = isOwner && (
    <CancelBid
      bidId={offer.id}
      mutate={refetch}
      trigger={
        <Button
          variant="secondary"
          py="space.4"
          px="space.16"
          fontSize="xs"
          lineHeight="3xl"
          ml="space.12"
          flexShrink={0}
        >
          {t('offers.table.item.cancel')}
        </Button>
      }
    />
  );

  if (isSingleOrder(offer))
    return (
      <AccountTableItemMobile
        mediaItemProps={{
          href: routes.nft.detail(
            collectionId,
            offer.criteria?.data?.token?.tokenId ?? '',
            marketplaceChain.routePrefix,
          ),
          title: offer.criteria?.data?.token?.name || `#${offer.criteria?.data?.token?.tokenId}`,
          imageSrc: offer.criteria?.data?.token?.image || offer.criteria?.data?.collection?.image,
          subtitle: (
            <Text fontSize="sm" color="gray.5" noOfLines={1} wordBreak="break-all">
              {formatAddress(offer.criteria?.data?.collection?.name)}
            </Text>
          ),
          quantity: offer.quantityRemaining,
          ...(!offer.criteria?.data?.token?.image &&
            offer.criteria?.data?.collection?.image && { rounded: 'full', coverImage: true }),
        }}
        icon={{ Component: OfferIcon, label: tActivity('event-titles.bid') }}
        currency={offer.price?.currency?.symbol as TokenSymbol}
        tokenAmount={offer.price?.amount?.decimal}
        startTime={offer.createdAt}
        startLabel={t('offers.table.item.created')}
        endTime={offer.validUntil ? new Date(offer.validUntil * 1000).toISOString() : undefined}
        endLabel={t('offers.table.item.expires')}
        quantity={offer.quantityRemaining}
      >
        {CancelButton}
      </AccountTableItemMobile>
    );

  return (
    <AccountTableItemMobile
      mediaItemProps={{
        href: routes.collections.detail(collectionId, marketplaceChain.routePrefix),
        title: formatAddress(offer.criteria?.data?.collection?.name),
        imageSrc: offer.criteria?.data?.collection?.image,
        subtitle: isAttributeOffer(offer) ? (
          <>
            <Button
              fontSize="xs"
              px={0}
              py="space.4"
              variant="clear"
              h="unset"
              rightIcon={
                <ChevronDownIcon
                  boxSize={rem(12)}
                  transition="transform 120ms ease-in-out"
                  transform={isOpen ? 'rotate(-180deg)' : ''}
                />
              }
              iconSpacing="space.4"
              onClick={onToggle}
              _hover={{ opacity: 0.6 }}
            >
              {tActivity('table.attributes.label')}
            </Button>
            <Collapse in={isOpen}>
              {/* @ts-expect-error - Type is wrong */}
              <CollectionOfferTraits fontSize="xs" traits={[offer.criteria?.data.attribute]} />
            </Collapse>
          </>
        ) : undefined,
        hideLinkOnSubtitle: true,
        quantity: offer.quantityRemaining,
        rounded: 'full',
        coverImage: true,
      }}
      icon={{
        Component: CollectionOfferIcon,
        label: isAttributeOffer(offer)
          ? tActivity('event-titles.attribute-offer')
          : tActivity('event-titles.collection-bid'),
      }}
      currency={offer.price?.currency?.symbol as TokenSymbol}
      tokenAmount={offer.price?.amount?.decimal}
      startTime={offer.createdAt}
      startLabel={t('offers.table.item.created')}
      endTime={offer.validUntil ? new Date(offer.validUntil * 1000).toISOString() : undefined}
      endLabel={t('offers.table.item.expires')}
      quantity={offer.quantityRemaining}
    >
      {CancelButton}
    </AccountTableItemMobile>
  );
};
